import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useModalControl } from "../../customeHooks/useModalControl";
import { useWindowDimensions } from "../../customeHooks/useWindowDimensions";
import { useGetUserProfileQuery } from "../../redux/features/auth/auth.api";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { useGetPayoutAccountQuery } from "../../redux/features/payment/payment.api";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { persistor } from "../../redux/provider";
import { regexText, sendSupportEmail } from "../../utils/helpers";
import { Button } from "../buttons/button";
import { Icon } from "../icon/icon";
import { MobileNavModal } from "../modal/app_modals/mobileNavModal";
import { ScrollToTop } from "../scrollToTop";
import { CreateEventButton } from "./components/app/createEventButton";
import { HelpWidget } from "./components/app/helpWidget";
import { ProfileDropdown } from "./components/app/profile";

const testArray = [
  /^\/events\/[^/]+\/published$/,
  /^\/events\/.+\/add-payout\/.+$/,
  /^\/complete-profile$/,
  /^\/events\/[^/]+\/$/,
  /^\/events\/[^/]+\/preview$/,
];

const mobileTestArray = [...testArray, /^\/events\/[^/]+$/];

const navs = [
  {
    title: "Home",
    to: "/home",
    icon: "home",
  },
  {
    title: "Events",
    to: "/events",
    icon: "calendar",
  },

  {
    title: "Discounts",
    to: "/discounts",
    icon: "circle-percent",
  },
];

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const pageRef = useRef<HTMLDivElement>(null);
  const helpWidgetRef = useRef<HTMLButtonElement>(null);

  const location = useLocation();

  const { scrollYProgress } = useScroll({ container: pageRef });

  useMotionValueEvent(scrollYProgress, "change", (val) => {
    setScrollProgress(val);
  });

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { width } = useWindowDimensions();

  const { handleModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl();

  const { isSignedIn, profile } = useAppSelector((state) => state.auth);

  useGetPayoutAccountQuery(
    {},
    { refetchOnMountOrArgChange: true, skip: !isSignedIn },
  );

  useGetUserProfileQuery(
    {
      id: profile?._id ?? "",
    },
    { refetchOnMountOrArgChange: true, skip: !isSignedIn },
  );

  const dropDownOptions = [
    {
      icon: "avatar",
      label: "View Profile",
      onClick: () => {
        navigate("/settings");
      },
    },
    {
      icon: "circle-help",
      label: "Get Help",
      onClick: () => {
        handleModalClose();
        sendSupportEmail();
      },
    },
    {
      icon: "logout",
      label: "Logout",
      onClick: () => {
        dispatch(authSliceActions.signUserOut());

        persistor.purge();
      },
    },
  ];

  useEffect(() => {
    if (pageRef.current) {
      pageRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });

      setScrollProgress(0);
    }
  }, [location.pathname]);

  return (
    <>
      <ScrollToTop
        changeKey={location.pathname}
        rerunOnKeyChange
        scrollIntoView
      />
      <div className="flex h-screen max-h-[100dvh] flex-col bg-light-grey-bg">
        {/* Top nav  */}
        <div
          className={classNames(
            "border-b border-grey-100 py-6 md:border-b-0 md:py-6",
            scrollProgress > 0.1
              ? "md:!border-b md:shadow-sm md:shadow-grey-900/20"
              : "",
            "!z-[998] transition-all duration-300",
            "bg-light-grey-bg",
          )}
        >
          <div className="max-width">
            <div className="relative flex items-center justify-between gap-4">
              <Link
                to={"/home"}
                className="max-w-[80px] md:absolute md:left-0 md:max-w-max"
              >
                <img
                  src="/assets/logo_full.svg"
                  className="w-full"
                  alt="logo"
                />
              </Link>

              {isSignedIn &&
              !regexText(location.pathname, testArray) &&
              ![""].includes(location.pathname) ? (
                <div className="hidden flex-1 items-center justify-center gap-4 md:flex">
                  {navs.map((nav, idx) => {
                    const key = `${idx}-${nav.to}`;

                    return (
                      <NavLink
                        key={key}
                        to={nav.to ?? ""}
                        className={({ isActive }) => {
                          return `soft-shrink flex items-center gap-2.5 rounded-2xl px-5 py-3 font-bold ${isActive && "bg-white"} `;
                        }}
                      >
                        <Icon
                          svg={nav.icon}
                          height={20}
                          width={20}
                          className=""
                        />
                        {nav.title}
                      </NavLink>
                    );
                  })}
                </div>
              ) : (
                <span className="opacity-0">p</span>
              )}

              {isSignedIn ? (
                <>
                  <ProfileDropdown
                    className={"hidden md:absolute md:right-0 md:inline-block"}
                    useCookieProfile={false}
                    dropDownOptions={dropDownOptions}
                  />

                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-full bg-white px-3 py-2 text-sm font-bold md:absolute md:right-0 md:hidden"
                    onClick={() => {
                      handleModalOpen("mobileNavModal");
                    }}
                  >
                    <Icon svg="caret-down" width={24} height={24} />
                    <span>Menu</span>
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-3 md:absolute md:right-0">
                  <Button
                    type="button"
                    text="Log in"
                    onClick={() => {
                      navigate("/login");
                    }}
                    className="min-w-max max-w-fit"
                    variation="plain-grey"
                  />

                  <Button
                    type="button"
                    text="Sign Up"
                    onClick={() => {
                      navigate("/signup");
                    }}
                    className="min-w-max max-w-fit"
                    variation="primary"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* main screen  */}
        <div id="pageTopRefToScroll" className="h-0" />
        <div
          className="max-width flex flex-1 flex-col overflow-y-scroll pb-20"
          ref={pageRef}
          id="appMainBody"
        >
          <div className="h-max w-full py-8">{children}</div>
        </div>

        {/* bottom mobile nav  */}
        <div
          className={classNames(`max-width relative py-4 md:hidden`, {
            "!hidden": regexText(location.pathname, mobileTestArray),
          })}
        >
          <div className="!absolute !bottom-[calc(100%+12px)] !right-[30px]">
            {["/events", "/home"].includes(location.pathname) && width < 768 ? (
              <CreateEventButton />
            ) : undefined}
          </div>

          <div className="grid grid-cols-3 items-center overflow-x-scroll">
            {navs.map((nav, idx) => {
              const key = `${idx}-${nav.to}`;

              return (
                <NavLink
                  key={key}
                  to={nav.to ?? ""}
                  className={({ isActive }) => {
                    return `soft-shrink flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold ${isActive && "bg-white"} `;
                  }}
                >
                  <Icon svg={nav.icon} height={20} width={20} className="" />
                  {nav.title}
                </NavLink>
              );
            })}
          </div>
        </div>

        <MobileNavModal
          open={modalOpen && modal === "mobileNavModal"}
          handleClose={handleModalClose}
          options={dropDownOptions}
        />
      </div>

      <div className="!fixed bottom-4 right-4 z-[999] w-fit md:bottom-7 md:right-10">
        <HelpWidget helpWidgetRef={helpWidgetRef} />
      </div>
    </>
  );
};
